import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { IBizEntity, LoggerService } from '@optima-stride-ai/core';
import { BizEntityTypeCategory } from '@optima-stride-ai/core';
import { CollaboraService } from './collabora.service';
import { environment } from '../../environments/environment';
import { StorageKeys } from '../shared/enums/app-constants';
/**
 * Service responsible for handling file operations (opening, type detection, routing).
 * Consolidates file-type specific logic that was previously scattered in the component.
 * 
 * @responsibility
 * - Determine correct viewer for file types
 * - Route to appropriate viewer component
 * - Handle Collabora integration
 * - Manage file type detection
 */
@Injectable({
  providedIn: 'root'
})
export class FileHandlerService {
  private readonly collaboraService = inject(CollaboraService);
  private readonly logger = inject(LoggerService);

  /**
   * Open a file in the appropriate viewer based on its type
   * 
   * @param file - The file entity to open
   * @param selectedParentId - Optional parent ID for context
   * @returns The file category if successfully routed, null otherwise
   */
  openFile(file: IBizEntity, selectedParentId?: string): BizEntityTypeCategory | null {
    // Validate input
    if (!file || !file.type?.category) {
      this.logger.error(`❌ Invalid file: missing type or category - ${file?.name || 'unknown'}`);
      return null;
    }
    
    const category = file.type.category;
    
    // Check if it's a navigable folder - should not be handled as a file
    if (file.type.isAlwaysNavigable) {
      this.logger.debug('📁 Navigable folder detected, skipping file handler');
      return null;
    }

    // Route based on file category
    switch (category) {
      case BizEntityTypeCategory.HayagrivaFileImage:
        return this.openImageFile(file, selectedParentId);

      case BizEntityTypeCategory.HayagrivaFileVideo:
        return this.openVideoFile(file, selectedParentId);

      case BizEntityTypeCategory.HayagrivaFilePDF:
        return this.openPDFFile(file, selectedParentId);

      case BizEntityTypeCategory.HayagrivaFileMarkdown:
        return this.openMarkdownFile(file, selectedParentId);

      case BizEntityTypeCategory.HayagrivaFileWhiteboard:
        return this.openWhiteboardFile(file, selectedParentId);

      case BizEntityTypeCategory.HayagrivaFileDoc:
      case BizEntityTypeCategory.HayagrivaFileSpreadsheet:
      case BizEntityTypeCategory.HayagrivaFileCSV:
      case BizEntityTypeCategory.HayagrivaFilePresentation:
      case BizEntityTypeCategory.HayagrivaFileDrawing:
        return this.openCollaboraFile(file, category);

      default:
        // Fallback: Check if Collabora supports this file type
        // if (environment.collaboraEnabled && this.collaboraService.isFileSupported(file.name)) {
        //   return this.openCollaboraFile(file, BizEntityTypeCategory.HayagrivaFileDoc);
        // }
          if (environment.collaboraEnabled && this.collaboraService.isFileSupported(category)) {
          return this.openCollaboraFile(file, BizEntityTypeCategory.HayagrivaFileDoc);
        }
        
        this.logger.warn(`⚠️ Unsupported file type: ${category}`);
        return null;
    }
  }

  /**
   * Check if a file can be opened (has a supported viewer)
   */
  canOpenFile(file: IBizEntity): boolean {
    if (!file?.type?.category) return false;
    
    const category = file.type.category;
    
    // List of supported categories
    const supportedCategories = [
      BizEntityTypeCategory.HayagrivaFileImage,
      BizEntityTypeCategory.HayagrivaFileVideo,
      BizEntityTypeCategory.HayagrivaFilePDF,
      BizEntityTypeCategory.HayagrivaFileMarkdown,
      BizEntityTypeCategory.HayagrivaFileWhiteboard,
      BizEntityTypeCategory.HayagrivaFileDoc,
      BizEntityTypeCategory.HayagrivaFileSpreadsheet,
      BizEntityTypeCategory.HayagrivaFileCSV,
      BizEntityTypeCategory.HayagrivaFilePresentation,
      BizEntityTypeCategory.HayagrivaFileDrawing
    ];

    if (supportedCategories.includes(category)) {
      return true;
    }

    // Check Collabora support as fallback
    // return environment.collaboraEnabled && this.collaboraService.isFileSupported(file.name);
     return environment.collaboraEnabled && this.collaboraService.isFileSupported(category);
  }

  /**
   * Get the appropriate viewer route for a file type
   */
  getViewerRoute(category: BizEntityTypeCategory): string | null {
    switch (category) {
      case BizEntityTypeCategory.HayagrivaFileImage:
        return '/documents/images';
      case BizEntityTypeCategory.HayagrivaFileVideo:
        return '/documents/videos';
      case BizEntityTypeCategory.HayagrivaFilePDF:
        return '/documents/pdf';
      case BizEntityTypeCategory.HayagrivaFileMarkdown:
        return '/documents/markdown';
      case BizEntityTypeCategory.HayagrivaFileWhiteboard:
        return '/documents/diagrams';
      case BizEntityTypeCategory.HayagrivaFileDoc:
      case BizEntityTypeCategory.HayagrivaFileSpreadsheet:
      case BizEntityTypeCategory.HayagrivaFileCSV:
      case BizEntityTypeCategory.HayagrivaFilePresentation:
      case BizEntityTypeCategory.HayagrivaFileDrawing:
        return '/documents/collabora';
      default:
        return null;
    }
  }

  /**
   * Private helper methods for each file type
   */
  private openImageFile(file: IBizEntity, parentId?: string): BizEntityTypeCategory {
    this.logger.debug(`🖼️ Opening image file: ${file.name}`);
    return BizEntityTypeCategory.HayagrivaFileImage;
  }

  private openVideoFile(file: IBizEntity, parentId?: string): BizEntityTypeCategory {
    this.logger.debug(`🎥 Opening video file: ${file.name}`);
    return BizEntityTypeCategory.HayagrivaFileVideo;
  }

  private openPDFFile(file: IBizEntity, parentId?: string): BizEntityTypeCategory {
    this.logger.debug(`📄 Opening PDF file: ${file.name}`);
    return BizEntityTypeCategory.HayagrivaFilePDF;
  }

  private openMarkdownFile(file: IBizEntity, parentId?: string): BizEntityTypeCategory {
    this.logger.debug(`📝 Opening Markdown file: ${file.name} (ID: ${file.id})`);
    return BizEntityTypeCategory.HayagrivaFileMarkdown;
  }

  private openWhiteboardFile(file: IBizEntity, parentId?: string): BizEntityTypeCategory {
    this.logger.debug(`🎨 Opening Whiteboard file: ${file.name} (ID: ${file.id})`);
    return BizEntityTypeCategory.HayagrivaFileWhiteboard;
  }

  private openCollaboraFile(file: IBizEntity, category: BizEntityTypeCategory): BizEntityTypeCategory | null {
    if (!environment.collaboraEnabled) {
      this.logger.warn('⚠️ Collabora is not enabled');
      return null;
    }

    this.logger.debug(`📊 Opening Collabora file: ${file.name}, Category: ${category}`);
    
    // Store the entity ID in session storage for Collabora to use
    sessionStorage.setItem(StorageKeys.COLLABORA_BIZ_ENTITY_ID, file.id);
    
    return category;
  }

  /**
   * Determine if a file is a folder/navigable container
   */
  isNavigableContainer(file: IBizEntity): boolean {
    return file?.type?.isAlwaysNavigable === true ||
           file?.type?.category === BizEntityTypeCategory.HayagrivaLibrary ||
           file?.type?.category === BizEntityTypeCategory.HayagrivaLibFolder;
  }
}
